/**
 * ChatModulePane
 *
 * Stateless presentational view for the embedded chat module pane. This file is
 * the QUARANTINE locus for the `@/modules` import-boundary exception — the
 * pane-only module components (`EditableModuleTitle`, `ModuleMenu`,
 * `EmailOptions`, `ModuleRenderer`, `IntegrationAccountsModal`, `ManageAccountsModal`)
 * and the `isV2` provider check live here, not spread across the orchestrator.
 *
 * The orchestrator owns ALL state and threads every value as a prop; the modal
 * state (`showIntegrationAccountsModal`/`showManageAccountsModal`) stays
 * parent-owned (the parent reads it elsewhere) and is passed in — this is a
 * stateless view, not a stateful child.
 */
import { useState, useEffect } from 'react';
import type { EditableModuleTitleRef } from '@/modules/_infrastructure/EditableModuleTitle';
import { EditableModuleTitle } from '@/modules/_infrastructure/EditableModuleTitle';
import { ModuleMenu } from '@/modules/_infrastructure/ModuleMenu';
import { useCalendar24Hour } from '@/modules/calendar/lib/timeFormat';
import { EmailOptions } from '@/modules/email/EmailOptions';
import ModuleRenderer from '@/modules/_infrastructure/ModuleRenderer';
import { IntegrationAccountsModal } from '@/modules/_infrastructure/IntegrationAccountsModal';
import { ManageAccountsModal } from '@/modules/email/ManageAccountsModal';
import { isV2 } from '@/modules/email/provider-display';
import { MODULE_ALIASES, getModuleMeta, type Module } from '@/types/modules';
import {
  nextModuleTitleOverride,
  resolveModuleTitleOverride,
  type ModuleTitleOverride,
} from './module-pane-helpers';

export interface ChatModulePaneProps {
  selectedChatPaneModule: Module;
  activeConversationId: string;
  isMobile: boolean;
  modulePaneCardRef: React.RefObject<HTMLDivElement | null>;
  modulePaneTitleRef: React.RefObject<EditableModuleTitleRef | null>;
  selectedChatPaneModuleTitle: string;
  hideModulePane: () => void;
  moduleMenuCloseSignal: number;
  showIntegrationAccountsModal: boolean;
  showManageAccountsModal: boolean;
  setShowIntegrationAccountsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowManageAccountsModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ChatModulePane({
  selectedChatPaneModule,
  activeConversationId,
  isMobile,
  modulePaneCardRef,
  modulePaneTitleRef,
  selectedChatPaneModuleTitle,
  hideModulePane,
  moduleMenuCloseSignal,
  showIntegrationAccountsModal,
  showManageAccountsModal,
  setShowIntegrationAccountsModal,
  setShowManageAccountsModal,
}: ChatModulePaneProps) {
  const [use24Hour, setUse24Hour] = useCalendar24Hour();
  // The override slot is tagged with the module that produced it and only
  // surfaces while that module is on screen (#5581) — the pane is un-keyed and
  // outlives module switches, so an untagged value could paint one frame on the
  // next module. No passive reset-on-switch effect: the render gate below keeps
  // a foreign override from ever painting, and the sole producer (ChannelsModule)
  // clears its own override on unmount / DM-close via `nextModuleTitleOverride`'s
  // scoped clear.
  const [moduleTitleOverride, setModuleTitleOverride] = useState<ModuleTitleOverride | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setModuleTitleOverride((prev) => nextModuleTitleOverride(prev, detail));
    };
    window.addEventListener('xavior:module-title-override', handler);
    return () => window.removeEventListener('xavior:module-title-override', handler);
  }, []);

  return (
      <div
        ref={modulePaneCardRef}
        className={`chat-module-pane-card ${isMobile ? 'is-mobile-fullscreen' : ''}${selectedChatPaneModule.type === 'report' ? ' is-report-module' : ''}`}
      >
        <div className="chat-module-pane-header">
          <div className="chat-module-pane-header-left">
            <EditableModuleTitle
              ref={modulePaneTitleRef}
              key={selectedChatPaneModule.id}
              className="chat-module-pane-title"
              module={selectedChatPaneModule}
              overrideLabel={resolveModuleTitleOverride(moduleTitleOverride, selectedChatPaneModule.id)}
              conversationId={activeConversationId}
              fallbackLabel={selectedChatPaneModuleTitle || 'Module'}
            />
          </div>
          <div className="chat-module-pane-header-right">
            {(() => {
              const usesModuleSpecificOptions = false; // All modules use ModuleMenu now

              return (
                <>
                  {usesModuleSpecificOptions ? (
                    <button
                      type="button"
                      className="chat-module-pane-close-btn"
                      title="Module options"
                      onClick={(event) => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        window.dispatchEvent(new CustomEvent('xavior:open-module-options', {
                          detail: {
                            moduleId: selectedChatPaneModule.id,
                            anchorRect: {
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                              left: rect.left,
                              width: rect.width,
                              height: rect.height,
                            },
                          },
                        }));
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
                      </svg>
                    </button>
                  ) : (
                    <ModuleMenu
                      moduleId={selectedChatPaneModule.id}
                      moduleType={MODULE_ALIASES[selectedChatPaneModule.type] ?? selectedChatPaneModule.type}
                      moduleConfig={selectedChatPaneModule.config as Record<string, unknown> | undefined}
                      onArchived={hideModulePane}
                      conversationId={activeConversationId}
                      closeSignal={moduleMenuCloseSignal}
                      customItems={(() => {
                        const cType = MODULE_ALIASES[selectedChatPaneModule.type] ?? selectedChatPaneModule.type;
                        const chatMeta = getModuleMeta(selectedChatPaneModule.type as any);
                        const items: Array<{ label: string; icon?: React.ReactNode; onClick: (anchorRect?: DOMRect) => void; keepOpenOnClick?: boolean; disabled?: boolean; toggle?: boolean }> = [];
                        if (chatMeta?.about) {
                          items.push({
                            label: 'About',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:show-module-about', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                        }
                        // View Archived intentionally appended LAST below
                        // so module-specific actions surface first.
                        if (cType === 'email') {
                          items.push({
                            label: 'Email Tab Settings',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:email-open-settings', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Manage Accounts',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
                            onClick: () => setShowManageAccountsModal(true),
                          });
                        }
                        if (cType === 'event_list' || cType === 'calendar' || cType === 'drive') {
                          items.push({
                            label: 'Manage Accounts',
                            icon: isV2
                              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                              : <img src="/google-logo.svg" width="14" height="14" alt="Google" />,
                            onClick: () => isV2 ? setShowManageAccountsModal(true) : setShowIntegrationAccountsModal(true),
                          });
                        }
                        // 24-hour clock toggle (calendar only). Renders a blue
                        // slider showing the current state; keepOpenOnClick lets
                        // you see it flip.
                        if (cType === 'event_list' || cType === 'calendar') {
                          items.push({
                            label: '24h Clock',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>,
                            onClick: () => setUse24Hour(!use24Hour),
                            keepOpenOnClick: true,
                            toggle: use24Hour,
                          });
                        }
                        if (cType === 'health') {
                          items.push({
                            label: 'Manage Wearables',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="7" ry="7" /><line x1="12" y1="18" x2="12" y2="18.01" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:health-manage-wearables', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Sync Wearable',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:health-sync-wearable', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Set Objectives',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:health-set-objectives', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                        }
                        if (cType === 'contact_list') {
                          items.push({
                            label: 'Manage Accounts',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-manage-accounts', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Enhance with AI',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.39 5.26L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.61-1.74L12 2z" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-bulk-enhance', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Email Extract',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-discover-open', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Find duplicates',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-duplicates-open', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Column header names',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="18" rx="1" /><rect x="17" y="3" width="5" height="18" rx="1" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-template-open', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Import CSV',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-import-csv', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Export CSV',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-export-csv', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                          items.push({
                            label: 'Merge duplicates',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="9" r="4" /><circle cx="17" cy="15" r="4" /><line x1="9" y1="9" x2="17" y2="15" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-merge-duplicates', { detail: { moduleId: selectedChatPaneModule.id } })),
                          });
                        }
                        // Kanban board-scoped actions (Fields removed; rename/
                        // archive/export/import) moved to the per-board "···" menu
                        // in KanbanBoard's header, so they always act on the open
                        // board. The module menu keeps only About + View Archived.
                        // View Archived — surfaced first for contacts, pinned
                        // to the end elsewhere.
                        if (cType === 'task_list' || cType === 'kanban' || cType === 'kanban_board' || cType === 'contact_list' || cType === 'pages' || cType === 'place_list' || cType === 'places' || cType === 'drive') {
                          const isDrive = cType === 'drive';
                          const viewArchivedItem = {
                            label: 'View Archived',
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>,
                            onClick: () => window.dispatchEvent(new CustomEvent(isDrive ? 'xavior:toggle-archived-drive' : 'xavior:toggle-archived-tasks', { detail: { moduleId: selectedChatPaneModule.id } })),
                          };
                          if (cType === 'contact_list') items.unshift(viewArchivedItem);
                          else items.push(viewArchivedItem);
                        }
                        return items.length > 0 ? items : undefined;
                      })()}
                      extraContent={(() => {
                        const cType = MODULE_ALIASES[selectedChatPaneModule.type] ?? selectedChatPaneModule.type;
                        if (cType === 'email' || cType === 'event_list' || cType === 'calendar' || cType === 'drive') {
                          return (closeMenu: () => void) => (
                            <EmailOptions
                              moduleId={selectedChatPaneModule.id}
                              moduleType={cType}
                              conversationId={activeConversationId}
                              onClose={closeMenu}
                            />
                          );
                        }
                        return undefined;
                      })()}
                    />
                  )}
                  {/* The "Show chat" chevron used to live here; reopening a
                      docked chat now goes through the header chat launcher
                      (see layout/HeaderChatLauncher.tsx). */}
                  {!isMobile && (
                  <button
                    type="button"
                    className="chat-module-pane-close-btn"
                    title="Close module"
                    onClick={() => hideModulePane()}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                    </svg>
                  </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        <div className="chat-module-pane-body">
          <div className="chat-module-pane-content">
              <ModuleRenderer
                key={selectedChatPaneModule.id}
                module={selectedChatPaneModule}
                conversationId={activeConversationId}
              />
          </div>
        </div>
        {showIntegrationAccountsModal && (
          <IntegrationAccountsModal onClose={() => setShowIntegrationAccountsModal(false)} moduleId={selectedChatPaneModule?.id} />
        )}
        {showManageAccountsModal && (
          <ManageAccountsModal onClose={() => setShowManageAccountsModal(false)} moduleId={selectedChatPaneModule?.id} />
        )}
      </div>
  );
}
